import {
  getMe,
  hasCognitoSession,
  isProblemError,
  type ErrorCode,
  type Me,
} from '@volee/api-client'
import { queryOptions, type QueryClient } from '@tanstack/react-query'

import { api } from '../services/api.ts'

// The current user is a query, not a store (§4.4): two copies would have to be realigned
// on every token refresh, role change and logout.

export const ME_KEY = ['me'] as const

/** Why a verified token cannot use the back office. Values first, as `ROLES`/`Role` does. */
export const REFUSAL_REASONS = ['not-linked', 'deactivated', 'refused'] as const

export type RefusalReason = (typeof REFUSAL_REASONS)[number]

/** What `/v1/me` answered. `refused` is a **verified** token whose account cannot use the
 * app: a session exists. Why it is a union: apps/admin-panel/docs/401-e-403-risposte-diverse.md. */
export type MeState =
  | { readonly kind: 'user'; readonly me: Me }
  | { readonly kind: 'anonymous' }
  | { readonly kind: 'refused'; readonly reason: RefusalReason }

// Matched explicitly: an unrecognised 403 — a proxy, a WAF, a newer code — must not be told
// it is an unlinked account.
function refusalFor(code: ErrorCode | null): RefusalReason {
  if (code === 'auth.account_not_linked') return 'not-linked'
  if (code === 'auth.account_deactivated') return 'deactivated'
  return 'refused'
}

/** One cache entry, shared by the guard and the components. 401 and 403 are mapped here so
 * no reader classifies them again; anything else throws — a backend down is not a refusal. */
export const meQueryOptions = queryOptions<MeState>({
  queryKey: ME_KEY,
  queryFn: async (): Promise<MeState> => {
    try {
      return { kind: 'user', me: await getMe(api) }
    } catch (error) {
      if (isProblemError(error) && error.status === 401) return { kind: 'anonymous' }
      if (isProblemError(error) && error.status === 403) {
        return { kind: 'refused', reason: refusalFor(error.code) }
      }
      throw error
    }
  },
  retry: false,
  staleTime: 5 * 60 * 1000,
})

// `ensureQueryData`, so moving between protected routes does not refetch on every transition.
export function loadMe(queryClient: QueryClient): Promise<MeState> {
  return queryClient.ensureQueryData(meQueryOptions)
}

/** The user, when there is one. Saves every reader a `kind` check to reach the fields. */
export function userOf(state: MeState | undefined): Me | undefined {
  return state?.kind === 'user' ? state.me : undefined
}

/** The user whose Cognito identity the account pages act on: absent under the dev bypass,
 *  where no token was ever minted and both changing a password and enrolling a factor would
 *  fail on a button that promised otherwise. */
export function cognitoUserOf(state: MeState | undefined): Me | undefined {
  const user = userOf(state)
  return user !== undefined && hasCognitoSession(user) ? user : undefined
}

/** Whether there is a session to end. True for a refusal, false under the dev bypass — see
 * apps/admin-panel/docs/utente-corrente-ha-tre-stati.md. Asked by every surface offering a way out,
 * so the answer cannot differ. */
export function canSignOut(state: MeState | undefined): boolean {
  if (state === undefined) return false
  if (state.kind === 'refused') return true
  return state.kind === 'user' && hasCognitoSession(state.me)
}

/** Whether the caller may invite staff. Why `staff_role` and not a `Role`:
 * apps/admin-panel/docs/pattern-di-una-feature.md § «L'invito, e le sue sei decisioni». */
export function isStaffAdmin(state: MeState | undefined): boolean {
  return userOf(state)?.staff_role === 'admin'
}
